import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ScanLine, BookOpen, Users, LogOut, Shield, Check, Mail, CreditCard, Loader2, Image as ImageIcon, Trash2, Upload, ChevronRight, FileText, PenLine } from "lucide-react";
import api, { apiError, API_ORIGIN, API } from "../lib/api";
import { formatPrice } from "./Subscribe";
import { SenderNote } from "./NewReport";
import { PlanCards, CheckoutSummary, useCheckout, ActivePlan, PLAN_NAME, planPrice } from "../components/PlanPicker";
import { canPlan } from "../lib/pro";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang, LangToggle } from "../context/LanguageContext";

const roleLabel = { super_admin: "Super Admin", manager: "Manager", mechanic: "Mechanic", office: "Office" };
const billingLabel = { trialing: "Free trial", active: "Active", expired: "Expired", granted: "Included", past_due: "Payment failed", canceled: "Canceled" };
const billingBadge = {
  trialing: "bg-amber-100 text-amber-700",
  active: "bg-emerald-100 text-emerald-700",
  granted: "bg-emerald-100 text-emerald-700",
  expired: "bg-red-100 text-red-700",
  past_due: "bg-red-100 text-red-700",
  canceled: "bg-zinc-200 text-zinc-600",
};
function billingDetail(b, t) {
  const d = (s) => (s ? s.slice(0, 10) : "");
  switch (b.status) {
    case "trialing": return `${t("Free trial — ends")} ${d(b.trial_ends_at)}`;
    case "active": return `${t("Active — renews")} ${d(b.current_period_end)}`;
    case "granted": return t("Included by the platform");
    case "past_due": return t("Last payment failed — please update billing");
    case "canceled": return t("Subscription canceled");
    case "expired": return t("Trial ended — subscribe to continue");
    default: return "";
  }
}

export default function Profile() {
  const { user, logout, setUser } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [recipients, setRecipients] = useState([]);
  const [billing, setBilling] = useState(null);
  const [nda, setNda] = useState(null);
  const [plan, setPlan] = useState("standard");
  const [forceCheckout, setForceCheckout] = useState(false);
  const checkout = useCheckout();
  const [defId, setDefId] = useState(user?.default_recipient_id || "");
  const [branding, setBranding] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInput = useRef(null);
  const initials = (user?.name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const isManager = user?.role === "manager" || user?.role === "super_admin";

  useEffect(() => { api.get("/recipients").then(({ data }) => setRecipients(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/legal/nda/status").then(({ data }) => setNda(data)).catch(() => {}); }, []);
  useEffect(() => { if (isManager) api.get("/company/branding").then(({ data }) => setBranding(data)).catch(() => {}); }, [isManager]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) return toast.error(t("Please choose a PNG or JPG."));
    if (file.size > 2 * 1024 * 1024) return toast.error(t("Logo must be 2 MB or smaller."));
    setLogoBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/company/branding");
      setBranding(data);
      toast.success(t("Logo updated"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm(t("Remove the company logo?"))) return;
    setLogoBusy(true);
    try {
      await api.delete("/company/logo");
      const { data } = await api.get("/company/branding");
      setBranding(data);
      toast.success(t("Logo removed"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setLogoBusy(false); }
  };

  const viewSignature = async () => {
    try {
      const r = await api.get("/legal/nda/signature.png", { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const pickDefault = async (id) => {
    setDefId(id);
    try { await api.put("/auth/me/default-recipient", { recipient_id: id }); setUser({ ...user, default_recipient_id: id }); toast.success(t("Default recipient updated")); }
    catch (e) { toast.error(apiError(e)); }
  };

  const managerRows = [
    { icon: ScanLine, title: "Parts Catalog", sub: "Manage parts & QR codes", to: "/parts-catalog" },
    { icon: Mail, title: "Office Recipients", sub: "Add or edit recipients", to: "/recipients" },
    { icon: BookOpen, title: "Spare Parts Book", sub: "Upload & browse the parts book", to: "/parts" },
    { icon: Users, title: "Manage Team", sub: "Invite & manage users", to: "/team" },
  ];

  return (
    <>
      <PageHeader title={t("Profile")} />
      <div className="px-4 pt-4">
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#F26722] text-white flex items-center justify-center font-black text-2xl shadow-lg shadow-[#F26722]/20">{initials}</div>
          <div className="min-w-0">
            <h2 className="text-xl font-extrabold text-white truncate" data-testid="profile-name">{user?.name}</h2>
            <p className="text-sm text-zinc-400 truncate">{user?.email}</p>
            <div className="flex gap-2 mt-2">
              {user?.company_name && <span className="inline-flex items-center gap-1.5 bg-[#3B1D0E] text-[#F97316] text-xs px-2.5 py-1 rounded-full font-medium"><Building2 size={12} /> {user.company_name}</span>}
              <span className="inline-flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 text-zinc-200 text-xs px-2.5 py-1 rounded-full font-medium">{t(roleLabel[user?.role]) || user?.role}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="profile-lang-row">
          <span className="text-sm font-semibold text-white">{t("Language")}</span>
          <LangToggle />
        </div>

        {billing && (
          <>
            <span className="text-[11px] font-bold tracking-wider text-[#3D6080] uppercase mt-7 mb-2 block">{t("Subscription")}</span>
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="billing-card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full ${billingBadge[billing.status] || "bg-zinc-200 text-zinc-600"}`} data-testid="billing-status">{t(billingLabel[billing.status]) || billing.status}</span>
                  <p className="text-sm text-zinc-500 mt-2" data-testid="billing-detail">{billingDetail(billing, t)}</p>
                  {(billing.status === "active" || billing.pro_granted) && (
                    <p className="text-sm font-bold text-white mt-2 flex items-center gap-1.5" data-testid="billing-current-plan">{t("Current plan")}: {billing.pro_granted ? t("Pro (granted)") : PLAN_NAME[billing.plan === "pro" ? "pro" : "standard"]}</p>
                  )}
                  {billing.status === "active" && billing.active_users != null && (
                    <p className="text-xs text-zinc-500 mt-0.5" data-testid="billing-active-users">{billing.active_users} {billing.active_users === 1 ? t("user") : t("users")} · {formatPrice(planPrice(billing, billing.plan), billing.currency)} / {t("user")} / {t(billing.interval === "month" ? "month" : billing.interval) || "month"}</p>
                  )}
                </div>
                <CreditCard size={22} className="text-[#F26722] shrink-0" />
              </div>

              {billing.status === "active" && !forceCheckout && <ActivePlan billing={billing} setBilling={setBilling} onNeedCheckout={() => setForceCheckout(true)} />}

              {billing.can_manage === true && billing.status !== "granted" && (billing.status !== "active" || forceCheckout) && billing.payments_configured !== false && billing.seat_amount_cents != null && (
                <div className="mt-3 border-t border-zinc-800 pt-3" data-testid="billing-cost-breakdown">
                  <PlanCards billing={billing} value={plan} onChange={setPlan} />
                  <CheckoutSummary billing={billing} plan={plan} />
                  <button onClick={() => checkout.start(plan)} disabled={checkout.loading} data-testid="manage-billing-btn"
                    className="w-full mt-3 bg-[#F26722] text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#D95513] disabled:opacity-60 transition-colors">
                    {checkout.loading ? <Loader2 className="animate-spin" size={16} /> : <><CreditCard size={16} /> {t("Subscribe now")}</>}
                  </button>
                </div>
              )}
              {billing.can_manage === true && billing.status !== "granted" && billing.payments_configured === false && (
                <p className="text-xs text-zinc-500 mt-3" data-testid="billing-not-configured">{t("Online payments are not switched on yet.")}</p>
              )}
              {billing.can_manage === false && (
                <p className="text-xs text-zinc-500 mt-3" data-testid="billing-manager-note">{t("Your manager handles billing.")}</p>
              )}
            </div>
          </>
        )}

        {nda && (
          <>
            <span className="text-[11px] font-bold tracking-wider text-[#3D6080] uppercase mt-7 mb-2 block">{t("Service agreement (NDA & AVG)")}</span>
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="nda-card">
              {nda.signed ? (
                <>
                  <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="nda-status">{t("Agreement signed")}</span>
                  <p className="text-sm text-zinc-500 mt-2" data-testid="nda-detail">{t("Signed by")} {nda.signed_by_name} {t("on")} {(nda.signed_at || "").slice(0, 10)} ({(nda.language || "").toUpperCase()})</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <a href={`${API}/legal/nda/pdf?lang=${nda.language || "en"}`} target="_blank" rel="noreferrer" data-testid="nda-view-pdf" className="bg-[#18181B] border border-zinc-800 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><FileText size={15} /> {t("View agreement (PDF)")}</a>
                    <button onClick={viewSignature} data-testid="nda-view-signature" className="bg-[#18181B] border border-zinc-800 text-zinc-200 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><PenLine size={15} /> {t("View signature")}</button>
                  </div>
                </>
              ) : (
                <>
                  <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="nda-status">{t("Not signed yet")}</span>
                  <p className="text-sm text-zinc-500 mt-2">{t("This agreement still needs to be signed by the billing owner.")}</p>
                  {nda.can_sign && (
                    <button onClick={() => nav("/sign-agreement")} data-testid="nda-sign-btn" className="w-full mt-3 bg-[#F26722] text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#D95513] transition-colors"><PenLine size={16} /> {t("Sign the agreement to continue")}</button>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {recipients.length > 0 && (
          <>
            <span className="text-[11px] font-bold tracking-wider text-zinc-400 uppercase mt-7 mb-2 block">{t("Default Office Recipient")}</span>
            <p className="text-xs text-zinc-500 mb-2">{t("Pre-selected on new reports to save time.")}</p>
            <SenderNote className="mb-2" />
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl divide-y divide-zinc-800/60">
              {recipients.map((o) => (
                <button key={o.id} data-testid={`default-recipient-${o.id}`} onClick={() => pickDefault(o.id)} className="w-full flex items-center justify-between p-4 text-left">
                  <div><p className="font-bold text-white">{o.name}</p><p className="text-xs text-zinc-500">{o.email}</p></div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${defId === o.id ? "border-[#F26722] bg-[#F26722]" : "border-zinc-600"}`}>{defId === o.id && <Check size={12} className="text-white" />}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {user?.role !== "office" && (
          <button onClick={() => nav("/scans")} data-testid="scans-row" className="w-full flex items-center justify-between p-3.5 bg-[#18181B] hover:bg-[#222226] border border-zinc-800 rounded-xl mt-4 transition-colors">
            <div className="flex items-center gap-3"><ScanLine size={22} className="text-[#F26722]" /><div className="text-left"><p className="font-bold text-white text-sm">{t("Barcode Scans")}</p><p className="text-xs text-zinc-500">{t("Log scanned parts to an office")}</p></div></div>
            <span className="text-zinc-600">›</span>
          </button>
        )}

        {canPlan(user) && user?.role !== "super_admin" && (
          <button onClick={() => nav("/checklists")} data-testid="profile-checklists" className="w-full flex items-center justify-between p-3.5 bg-[#18181B] hover:bg-[#222226] border border-zinc-800 rounded-xl mt-4 transition-colors">
            <div className="flex items-center gap-3"><FileText size={22} className="text-[#F26722]" /><div className="text-left"><p className="font-bold text-white text-sm">{t("Checklists")}</p><p className="text-xs text-zinc-500">{t("One checklist template per machine type")}</p></div></div>
            <ChevronRight size={18} className="text-zinc-600" />
          </button>
        )}

        {["manager", "office", "super_admin"].includes(user?.role) && (
          <button onClick={() => nav("/machine-types")} data-testid="profile-machine-types" className="w-full flex items-center justify-between p-3.5 bg-[#18181B] hover:bg-[#222226] border border-zinc-800 rounded-xl mt-4 transition-colors">
            <div className="flex items-center gap-3"><Building2 size={22} className="text-[#F26722]" /><div className="text-left"><p className="font-bold text-white text-sm">{t("Machine Types")}</p><p className="text-xs text-zinc-500">{t("Manage machine types & their parts books")}</p></div></div>
            <ChevronRight size={18} className="text-zinc-600" />
          </button>
        )}

        {isManager && (
          <>
            <span className="text-[11px] font-bold tracking-wider text-zinc-400 uppercase mt-7 mb-2 block">{t("Company Branding")}</span>
            <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="company-branding">
              <div className="bg-white rounded-xl h-24 flex items-center justify-center overflow-hidden">
                {branding?.has_logo && branding?.logo_url
                  ? <img src={`${API_ORIGIN}${branding.logo_url}`} alt="Company logo" className="max-h-20 max-w-full object-contain" data-testid="company-logo-preview" />
                  : <ImageIcon size={26} className="text-zinc-300" />}
              </div>
              {!branding?.has_logo && <p className="text-xs text-zinc-500 mt-2" data-testid="no-logo-note">{t("No logo uploaded yet.")}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => logoInput.current?.click()} disabled={logoBusy} data-testid="upload-logo-btn"
                  className="bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5">
                  {logoBusy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {branding?.has_logo ? t("Replace logo") : t("Upload logo")}
                </button>
                {branding?.has_logo && (
                  <button onClick={removeLogo} disabled={logoBusy} data-testid="remove-logo-btn"
                    className="bg-[#18181B] border border-zinc-800 text-red-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Trash2 size={16} /> {t("Remove")}</button>
                )}
                <input ref={logoInput} type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadLogo} />
              </div>
              <p className="text-xs text-zinc-500 mt-2">{t("A wide/landscape logo looks best; it appears on report PDFs and office emails.")}</p>
            </div>

            <span className="text-[11px] font-bold tracking-wider text-zinc-400 uppercase mt-7 mb-2 block">{t("Manager")}</span>
            {managerRows.map((r) => (
              <button key={r.title} data-testid={`manager-row-${r.to.slice(1)}`} onClick={() => nav(r.to)} className="w-full flex items-center justify-between p-3.5 bg-[#18181B] hover:bg-[#222226] border border-zinc-800 rounded-xl mb-2 transition-colors">
                <div className="flex items-center gap-3"><r.icon size={22} className="text-[#F26722]" /><div className="text-left"><p className="font-bold text-white text-sm">{t(r.title)}</p><p className="text-xs text-zinc-500">{t(r.sub)}</p></div></div>
                <span className="text-zinc-600">›</span>
              </button>
            ))}
          </>
        )}

        {user?.role === "super_admin" && (
          <button onClick={() => nav("/admin")} className="w-full flex items-center gap-3 p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl mt-2 mb-2" data-testid="go-admin-btn">
            <Shield size={22} className="text-amber-500" /><div className="text-left"><p className="font-bold text-amber-400 text-sm">{t("Super-Admin Console")}</p><p className="text-xs text-zinc-500">{t("Cross-company control")}</p></div>
          </button>
        )}

        <button data-testid="logout-btn" onClick={logout} className="w-full flex items-center justify-center gap-2 p-3.5 bg-[#18181B] border border-zinc-800 rounded-xl mt-4 text-red-400 font-semibold"><LogOut size={18} /> {t("Log out")}</button>
      </div>
    </>
  );
}
